import { useApolloClient } from '@apollo/client'
import type { GuestDestination } from '@doss/configbase-common/apps'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { GuestDestinationHrefDocument } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import type { GuestDestinationRequest, HostNavigationRequest } from './GuestFrame'

/**
 * Hand a mailto:/tel: href to the OS protocol handler without unloading the page. This
 * document holds transient activation when a guest-requested link arrives (ancestor
 * propagation from the guest's click), which satisfies the browser's external-protocol
 * gesture requirement — reliability matches a native mailto/tel link in the host.
 */
export const launchProtocolHandler = (href: string): void => {
  window.location.assign(href)
}

/**
 * Open a new tab for a guest-requested link.
 * When a popup is blocked, fallback to a toast with the link.
 * The originating click happens inside the guest iframe, but transient user activation
 * propagates to ancestor documents (User Activation v2), so this document normally holds
 * activation when the request arrives and window.open succeeds outright. The snackbar covers
 * the rare genuinely-blocked case (aggressive blocker settings, consumed or expired
 * activation): its link renders a real anchor (target="_blank" rel="noopener noreferrer"),
 * and the user's snackbar click carries fresh activation.
 *
 * The opener is severed manually rather than via a `noopener` features string: per spec,
 * window.open returns null whenever the features include noopener — on success too — which makes
 * a blocked popup indistinguishable from a successful open. Opening bare and nulling `opener` on
 * the returned window is race-free (at return it is still the initial same-origin about:blank).
 * The features-string `noreferrer` is lost with this pattern; the document's referrer policy
 * governs instead (the modern default sends at most the origin cross-origin).
 */
export const useOpenTabWithFallback = (): ((href: string) => void) => {
  const { t } = useTypedTranslation()
  const { showInfo } = useDossSnackbar()
  return useCallback(
    (href) => {
      const opened = window.open(href, '_blank')
      if (opened === null) {
        showInfo(t('embed.popupBlocked'), href)
        return
      }
      opened.opener = null
    },
    [showInfo, t],
  )
}

/**
 * Host-side handling for same-origin links requested from a guest frame:
 * SPA-navigate in place, or open a new tab (with the popup fallback) for modified clicks.
 */
export const useHostNavigationHandler = (): ((link: HostNavigationRequest) => void) => {
  const navigate = useNavigate()
  const openTab = useOpenTabWithFallback()
  return useCallback(
    ({ pathname, search, newTab }) => {
      if (newTab) {
        openTab(`${pathname}${search}`)
        return
      }
      void navigate({ pathname, search })
    },
    [navigate, openTab],
  )
}

/**
 * The existing host route a guest's link out of its app names by logical id, or null when the
 * viewer cannot read the target or it does not exist. One cache serves hover pre-resolution and
 * the click, so a link resolved on hover navigates without another round-trip.
 */
export const useGuestDestinationResolver = (): ((
  destination: GuestDestination,
) => Promise<string | null>) => {
  const client = useApolloClient()
  return useCallback(
    async (destination) => {
      const { data } = await client.query({
        query: GuestDestinationHrefDocument,
        variables: {
          input: {
            kind: destination.kind,
            logicalId: destination.logicalId,
            ...(destination.path === undefined ? {} : { path: destination.path }),
            ...(destination.search === undefined ? {} : { search: destination.search }),
          },
        },
      })
      return data?.guestDestinationHref ?? null
    },
    [client],
  )
}

/**
 * Host-side handling for a guest's link out of its app by logical id: resolve the existing route
 * it names, then navigate there in place or in a new tab. The tab opens synchronously — while this
 * document still holds the guest click's propagated activation — and receives its location once
 * the id resolves, since opening only after the round-trip would be blocked. A target the viewer
 * cannot read, or one that no longer exists, closes the tab and says so.
 */
export const useGuestDestinationHandler = (): ((request: GuestDestinationRequest) => void) => {
  const resolve = useGuestDestinationResolver()
  const navigate = useNavigate()
  const { t } = useTypedTranslation()
  const { showInfo } = useDossSnackbar()
  return useCallback(
    ({ destination, newTab }) => {
      const tab = newTab ? window.open('', '_blank') : null
      if (tab !== null) {
        tab.opener = null
      }
      const unavailable = (): void => {
        tab?.close()
        showInfo(t('embed.linkUnavailable'))
      }
      resolve(destination)
        .then((href) => {
          if (href === null) {
            unavailable()
            return
          }
          if (tab !== null) {
            tab.location.href = href
            return
          }
          if (newTab) {
            // The placeholder tab was blocked; the snackbar's link carries fresh activation.
            showInfo(t('embed.popupBlocked'), href)
            return
          }
          void navigate(href)
        })
        .catch(unavailable)
    },
    [navigate, resolve, showInfo, t],
  )
}
